import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseAccordionItem, BaseAccordionProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

export function validateBaseAccordionProps({
  items,
  value,
  defaultValue,
  allowCollapse,
  keyboardNavigation,
}: Pick<
  BaseAccordionProps,
  | 'allowCollapse'
  | 'defaultValue'
  | 'items'
  | 'keyboardNavigation'
  | 'value'
>) {
  if (!Array.isArray(items)) {
    throw new TypeError('BaseAccordion items must be an array.');
  }
  if (value !== undefined && (typeof value !== 'string' || !value.trim())) {
    throw new TypeError(
      'BaseAccordion value must be a non-empty string when provided.',
    );
  }
  if (
    defaultValue !== undefined &&
    (typeof defaultValue !== 'string' || !defaultValue.trim())
  ) {
    throw new TypeError(
      'BaseAccordion defaultValue must be a non-empty string when provided.',
    );
  }
  if (typeof allowCollapse !== 'boolean') {
    throw new TypeError('BaseAccordion allowCollapse must be a boolean.');
  }
  if (typeof keyboardNavigation !== 'boolean') {
    throw new TypeError('BaseAccordion keyboardNavigation must be a boolean.');
  }

  const values = new Set<string>();
  items.forEach((item, index) => {
    if (!item || typeof item !== 'object') {
      throw new TypeError(
        `BaseAccordion item at index ${index} must be an object.`,
      );
    }
    if (typeof item.value !== 'string' || !item.value.trim()) {
      throw new TypeError(
        `BaseAccordion item value at index ${index} must be a non-empty string.`,
      );
    }
    if (values.has(item.value)) {
      throw new TypeError(
        'BaseAccordion items must use unique values; ' +
          `duplicate value ${JSON.stringify(item.value)} at index ${index}.`,
      );
    }
    if (item.disabled !== undefined && typeof item.disabled !== 'boolean') {
      throw new TypeError(
        `BaseAccordion item disabled at index ${index} must be a boolean.`,
      );
    }
    values.add(item.value);
  });
}

function toDomId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '-');
}

function getDomIdFragments(items: BaseAccordionItem[]) {
  const usedFragments = new Set<string>();

  return items.map((item, index) => {
    const baseFragment = toDomId(item.value) || `item-${index}`;
    let fragment = baseFragment;

    if (usedFragments.has(fragment)) {
      fragment = `${baseFragment}-${index}`;
      while (usedFragments.has(fragment)) {
        fragment = `${fragment}-${index}`;
      }
    }

    usedFragments.add(fragment);
    return fragment;
  });
}

export const BaseAccordion = forwardRef<HTMLDivElement, BaseAccordionProps>(
  function BaseAccordion(
    {
      items = [],
      value,
      defaultValue,
      allowCollapse = true,
      keyboardNavigation = true,
      appearance,
      className,
      classNames,
      style,
      onValueChange,
      id,
      onFocusCapture,
      onBlurCapture,
      ...accordionProps
    },
    ref,
  ) {
    validateBaseAccordionProps({
      items,
      value,
      defaultValue,
      allowCollapse,
      keyboardNavigation,
    });

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const generatedId = useId();
    const rootId = id ?? generatedId;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const [focusedValue, setFocusedValue] = useState<string>();
    const triggerRefs = useRef(new Map<string, HTMLButtonElement>());
    const ownsTriggerFocusRef = useRef(false);
    const isControlled = value !== undefined;
    const openValue = isControlled ? value : uncontrolledValue;
    const idFragments = getDomIdFragments(items);
    const enabledItems = items.filter((item) => !item.disabled);
    const openItem = items.find((item) => item.value === openValue);
    const layerDirection =
      typeof accordionProps.dir === 'string'
        ? accordionProps.dir
        : componentAttributes.dir;

    useEffect(() => {
      if (!ownsTriggerFocusRef.current || focusedValue === undefined) return;
      const focusedItem = items.find((item) => item.value === focusedValue);
      if (focusedItem && !focusedItem.disabled) return;

      const fallbackItem =
        enabledItems.find((item) => item.value === openValue) ?? enabledItems[0];
      if (!fallbackItem) {
        ownsTriggerFocusRef.current = false;
        return;
      }

      setFocusedValue(fallbackItem.value);
      triggerRefs.current.get(fallbackItem.value)?.focus();
    }, [enabledItems, focusedValue, items, openValue]);

    const handleToggle = (item: BaseAccordionItem) => {
      if (item.disabled) return;

      const isOpen = openValue === item.value;
      if (isOpen && !allowCollapse) {
        onValueChange?.(item.value);
        return;
      }

      const nextOpenValue = isOpen ? undefined : item.value;
      if (!isControlled) {
        setUncontrolledValue(nextOpenValue);
      }
      onValueChange?.(nextOpenValue);
    };

    const moveFocus = (
      event: KeyboardEvent<HTMLButtonElement>,
      item: BaseAccordionItem,
      step: number | 'first' | 'last',
    ) => {
      if (!keyboardNavigation || enabledItems.length === 0) return;

      let targetItem: BaseAccordionItem | undefined;
      if (step === 'first') {
        targetItem = enabledItems[0];
      } else if (step === 'last') {
        targetItem = enabledItems[enabledItems.length - 1];
      } else {
        const currentIndex = enabledItems.findIndex(
          (enabledItem) => enabledItem.value === item.value,
        );
        const nextIndex =
          (currentIndex + step + enabledItems.length) % enabledItems.length;
        targetItem = enabledItems[nextIndex];
      }

      if (!targetItem) return;
      event.preventDefault();
      setFocusedValue(targetItem.value);
      triggerRefs.current.get(targetItem.value)?.focus();
    };

    const handleKeyDown = (
      event: KeyboardEvent<HTMLButtonElement>,
      item: BaseAccordionItem,
    ) => {
      if (item.disabled) return;
      if (event.key === 'ArrowDown') {
        moveFocus(event, item, 1);
      } else if (event.key === 'ArrowUp') {
        moveFocus(event, item, -1);
      } else if (event.key === 'Home') {
        moveFocus(event, item, 'first');
      } else if (event.key === 'End') {
        moveFocus(event, item, 'last');
      }
    };

    const isOwnedTrigger = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      target.dataset.accordionOwner === rootId;

    const handleFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
      if (isOwnedTrigger(event.target)) {
        ownsTriggerFocusRef.current = true;
      }
      onFocusCapture?.(event);
    };

    const handleBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
      if (
        isOwnedTrigger(event.target) &&
        !isOwnedTrigger(event.relatedTarget)
      ) {
        ownsTriggerFocusRef.current = false;
      }
      onBlurCapture?.(event);
    };

    return (
      <div
        {...componentAttributes}
        {...accordionProps}
        ref={ref}
        id={id}
        dir={layerDirection}
        data-monkeys-component="accordion"
        data-slot="root"
        data-state={openItem ? 'open' : 'closed'}
        data-collapsible={allowCollapse}
        data-keyboard-navigation={keyboardNavigation}
        className={cn(theme.slots.accordionRoot, classNames?.root, className)}
        style={{
          ...theme.styles.accordion,
          ...style,
        }}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
      >
        {items.map((item, index) => {
          const itemId = idFragments[index];
          const triggerId = `${rootId}-${itemId}-trigger`;
          const panelId = `${rootId}-${itemId}-panel`;
          const open = item.value === openValue;
          const state = open ? 'open' : 'closed';

          return (
            <div
              {...componentAttributes}
              key={item.value}
              dir={layerDirection}
              data-monkeys-component="accordion"
              data-slot="item"
              data-state={state}
              data-disabled={item.disabled || undefined}
              className={cn(theme.slots.accordionItem, classNames?.item)}
            >
              <button
                {...componentAttributes}
                ref={(node) => {
                  if (node) {
                    triggerRefs.current.set(item.value, node);
                  } else {
                    triggerRefs.current.delete(item.value);
                  }
                }}
                id={triggerId}
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                aria-disabled={item.disabled || undefined}
                disabled={item.disabled}
                dir={layerDirection}
                data-accordion-owner={rootId}
                data-monkeys-component="accordion"
                data-slot="trigger"
                data-state={state}
                data-disabled={item.disabled || undefined}
                className={cn(
                  theme.slots.accordionTrigger,
                  classNames?.trigger,
                )}
                onFocus={() => setFocusedValue(item.value)}
                onKeyDown={(event) => handleKeyDown(event, item)}
                onClick={() => handleToggle(item)}
              >
                <span className="min-w-0 truncate">{item.title}</span>
                <span
                  aria-hidden="true"
                  data-monkeys-component="accordion"
                  data-slot="trigger-icon"
                  data-state={state}
                  className={cn(
                    theme.slots.accordionTriggerIcon,
                    open && 'rotate-[225deg]',
                    classNames?.triggerIcon,
                  )}
                />
              </button>
              {open ? (
                <div
                  {...componentAttributes}
                  id={panelId}
                  role="region"
                  aria-labelledby={triggerId}
                  dir={layerDirection}
                  data-monkeys-component="accordion"
                  data-slot="panel"
                  data-state="open"
                  className={cn(
                    theme.slots.accordionPanel,
                    classNames?.panel,
                  )}
                >
                  {hasRenderableNode(item.content) ? item.content : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  },
);
